import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, Leaf } from "lucide-react";
import { NAV, COMPANY } from "../data/content";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setProductsOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-cream/90 backdrop-blur-xl border-b border-border-soft"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-6 md:px-10 xl:px-16">
        <div className="flex items-center justify-between h-20 md:h-24">
          <Link
            to="/"
            data-testid="header-logo"
            className="flex items-center gap-2 group"
          >
            <span className={`relative flex items-center justify-center w-10 h-10 rounded-full transition-colors ${scrolled ? "bg-forest text-cream" : "bg-white/15 text-white border border-white/30"}`}>
              <Leaf size={18} strokeWidth={1.6} />
            </span>
            {/* Vertical Divider Line */}
            <span className={`h-8 w-px mx-1 transition-colors ${scrolled ? "bg-saffron/50" : "bg-white/40 shadow-sm"}`} />
            <span className="flex flex-col leading-none">
              <span
                className={`font-serif text-2xl tracking-tight transition-colors ${
                  scrolled ? "text-forest" : "text-white drop-shadow-md"
                }`}
              >
                {COMPANY.short}
              </span>
              <span
                className={`eyebrow mt-1 text-[10px] transition-colors ${
                  scrolled ? "text-saffron font-bold" : "text-white/90 drop-shadow-md font-bold"
                }`}
              >
                Private Limited
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            {NAV.map((item) =>
              item.children ? (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => setProductsOpen(true)}
                  onMouseLeave={() => setProductsOpen(false)}
                >
                  <button
                    data-testid="nav-products"
                    className={`flex items-center gap-1 text-sm font-medium tracking-wide transition-colors ${
                      scrolled ? "text-forest hover:text-saffron" : "text-cream hover:text-saffron"
                    }`}
                  >
                    {item.label}
                    <ChevronDown size={14} />
                  </button>
                  <div
                    className={`absolute left-1/2 -translate-x-1/2 top-full pt-4 transition-all duration-300 ${
                      productsOpen
                        ? "opacity-100 translate-y-0 pointer-events-auto"
                        : "opacity-0 -translate-y-2 pointer-events-none"
                    }`}
                  >
                    <div className="min-w-[220px] bg-cream border border-border-soft shadow-xl">
                      {item.children.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          data-testid={`nav-dropdown-${c.label.toLowerCase()}`}
                          className="block px-6 py-3 text-sm text-forest hover:bg-cream-2 hover:text-saffron transition-colors border-b last:border-b-0 border-border-soft"
                        >
                          {c.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `text-sm font-medium tracking-wide transition-colors ${
                      isActive
                        ? "text-saffron"
                        : scrolled
                        ? "text-forest hover:text-saffron"
                        : "text-cream hover:text-saffron"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="hidden lg:flex items-center">
            <Link
              to="/contact"
              data-testid="header-cta"
              className={scrolled ? "btn-primary" : "btn-accent"}
            >
              Get in touch
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            aria-label="Open menu"
            onClick={() => setMobileOpen((v) => !v)}
            className={`lg:hidden flex items-center justify-center w-11 h-11 border transition-colors ${
              scrolled || mobileOpen
                ? "border-forest/20 text-forest"
                : "border-cream/40 text-cream"
            }`}
          >
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height] duration-500 ease-out ${
          mobileOpen ? "max-h-[640px]" : "max-h-0"
        }`}
      >
        <div
          data-testid="mobile-menu"
          className="px-6 pb-8 pt-2 bg-cream border-t border-border-soft"
        >
          {NAV.map((item) =>
            item.children ? (
              <div key={item.label} className="py-2">
                <button
                  onClick={() => setProductsOpen((v) => !v)}
                  className="flex items-center justify-between w-full py-3 text-left text-forest"
                  data-testid="mobile-products-toggle"
                >
                  <span className="font-serif text-2xl">{item.label}</span>
                  <ChevronDown
                    size={18}
                    className={`transition-transform ${productsOpen ? "rotate-180" : ""}`}
                  />
                </button>
                {productsOpen && (
                  <div className="pl-4 pb-2">
                    {item.children.map((c) => (
                      <NavLink
                        key={c.to}
                        to={c.to}
                        data-testid={`mobile-nav-${c.label.toLowerCase()}`}
                        className="block py-2 text-base text-muted-ink hover:text-saffron"
                      >
                        {c.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `block py-3 font-serif text-2xl border-b border-border-soft ${
                    isActive ? "text-saffron" : "text-forest"
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
          <Link
            to="/contact"
            className="btn-primary mt-6 w-full"
            data-testid="mobile-header-cta"
          >
            Get in touch
          </Link>
        </div>
      </div>
    </header>
  );
};

export default Header;
